import React, { useRef, useState } from 'react';
import { normalizedPointFromImage } from './visualTemplateSelection';

export const regionFromPoints = (start, end) => ({
  x: Math.min(start.x, end.x),
  y: Math.min(start.y, end.y),
  width: Math.abs(end.x - start.x),
  height: Math.abs(end.y - start.y),
});

function Overlay({ region, colour }) {
  if (!region) return null;
  return <span style={{
    position: 'absolute',
    left: `${region.x * 100}%`,
    top: `${region.y * 100}%`,
    width: `${region.width * 100}%`,
    height: `${region.height * 100}%`,
    border: `3px solid ${colour}`,
    background: `${colour}22`,
    boxSizing: 'border-box',
    pointerEvents: 'none',
  }} />;
}

function ScreenshotRegionSelector({
  dataUrl,
  alt,
  active,
  regions = [],
  imageRef: externalImageRef,
  onRegion,
}) {
  const internalImageRef = useRef(null);
  const [start, setStart] = useState(null);
  const [draft, setDraft] = useState(null);
  const imageRef = externalImageRef || internalImageRef;

  const point = (event) => normalizedPointFromImage(
    event.clientX,
    event.clientY,
    imageRef.current
  );

  const handlePointerDown = (event) => {
    if (!active) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const next = point(event);
    setStart(next);
    setDraft(next ? { ...next, width: 0, height: 0 } : null);
  };

  const handlePointerMove = (event) => {
    if (!active || !start) return;
    const current = point(event);
    if (current) setDraft(regionFromPoints(start, current));
  };

  const finish = (event) => {
    if (!active || !start) return;
    const current = point(event);
    const region = current ? regionFromPoints(start, current) : draft;
    setStart(null);
    setDraft(null);
    if (region?.width >= 0.01 && region?.height >= 0.01) onRegion(region);
  };

  return (
    <div>
      {active && <p><strong>Drawing active:</strong> drag from one corner to the opposite corner on the screenshot.</p>}
      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: '100%',
          cursor: active ? 'crosshair' : 'default',
          touchAction: active ? 'none' : 'auto',
          userSelect: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finish}
        onPointerCancel={() => { setStart(null); setDraft(null); }}
      >
        <img
          ref={imageRef}
          src={dataUrl}
          alt={alt}
          draggable={false}
          style={{ maxWidth: '100%', display: 'block', pointerEvents: 'none' }}
        />
        {regions.map((entry) => (
          <Overlay key={entry.key} region={entry.region} colour={entry.colour || '#38bdf8'} />
        ))}
        <Overlay region={draft} colour="#fbbf24" />
      </div>
    </div>
  );
}

export default ScreenshotRegionSelector;
